/**
 * AI Audit modal — calls /api/projects/{id}/audit and renders the markdown report.
 */
import { useEffect, useMemo, useState } from "react";
import DOMPurify from "dompurify";
import { diffLines } from "diff";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Brain, X, Spinner, ArrowsClockwise, Copy, GitDiff } from "@phosphor-icons/react";
import { toast } from "sonner";

// DOMPurify config — strip all HTML except the lightweight markdown subset we
// emit ourselves below. No <script>, <iframe>, <object>, <embed>, no on*
// handlers, no javascript: URLs.
const SANITIZE_CFG = {
  ALLOWED_TAGS: ["h2","h3","p","strong","em","code","pre","ul","ol","li","span","br","a"],
  ALLOWED_ATTR: ["class","href","title","rel","target"],
  ALLOW_DATA_ATTR: false,
};

function mdToHtml(md) {
  // Lightweight markdown → HTML (headings, bullets, bold, italic, inline code).
  if (!md) return "";
  let html = md
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  // Code fences
  html = html.replace(/```([\s\S]*?)```/g,
    (_, code) => `<pre class="bg-black/60 border border-white/10 p-3 my-2 text-[11px] overflow-x-auto"><code>${code.trim()}</code></pre>`);
  // Headings
  html = html.replace(/^## (.+)$/gm, '<h3 class="font-display text-base text-[#00E5FF] mt-4 mb-1 uppercase tracking-widest">$1</h3>');
  html = html.replace(/^# (.+)$/gm,  '<h2 class="font-display text-lg text-white mt-3 mb-2">$1</h2>');
  // Checkboxes — explicit before bullets so [x] / [ ] are caught.
  html = html.replace(/^- \[x\] (.+)$/gim,
    '<li class="text-[#00E5FF]"><span class="inline-block w-4">✓</span>$1</li>');
  html = html.replace(/^- \[ \] (.+)$/gim,
    '<li class="text-gray-400"><span class="inline-block w-4">▢</span>$1</li>');
  // Bullets
  html = html.replace(/^- (.+)$/gm,  '<li class="ml-4 list-disc list-outside">$1</li>');
  // Bold + italic + inline code
  html = html.replace(/\*\*([^*]+)\*\*/g, '<strong class="text-white">$1</strong>');
  html = html.replace(/\*([^*]+)\*/g,     '<em>$1</em>');
  html = html.replace(/`([^`]+)`/g,       '<code class="px-1 py-0.5 bg-black/60 border border-white/10 text-[11px] text-[#00E5FF]">$1</code>');
  // Wrap consecutive <li> blocks into <ul>
  html = html.replace(/(<li[\s\S]*?<\/li>\n?)+/g, (m) => `<ul class="my-2 space-y-1 text-[13px]">${m}</ul>`);
  // Paragraphs (any remaining lines)
  html = html.split("\n\n").map((b) => /<[hu]/.test(b.trim()) ? b : `<p class="text-[13px] text-gray-300 my-2 leading-relaxed">${b}</p>`).join("\n");
  // Sanitize before returning — defence in depth even though the source is
  // escaped & we only emit a known-safe tag subset.
  return DOMPurify.sanitize(html, SANITIZE_CFG);
}

export default function AIAuditModal({ open, onClose, projectId }) {
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  // Compare/diff mode — when ON the body splits into a base/target diff view
  // built off two saved reports.
  const [compareMode, setCompareMode] = useState(false);
  const [baseId, setBaseId] = useState("");
  const [targetId, setTargetId] = useState("");

  const loadHistory = async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}/audit-history`);
      setHistory(data?.audits || []);
    } catch (err) {
      console.warn("Failed to load audit history:", err);
    }
  };

  const run = async () => {
    setBusy(true);
    setError("");
    setReport(null);
    try {
      const { data } = await api.post(`/projects/${projectId}/audit`);
      setReport(data.report);
      loadHistory();
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Audit failed.");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    loadHistory();
    if (!report && !busy) run();
    // eslint-disable-next-line
  }, [open]);

  const pickHistory = (rec) => { setReport(rec.report); setShowHistory(false); };

  // Once history is loaded, seed compare-mode selectors with the two most
  // recent reports so the diff is meaningful by default.
  useEffect(() => {
    if (!compareMode || history.length < 2) return;
    if (!targetId) setTargetId(history[0].id);
    if (!baseId)   setBaseId(history[1].id);
  }, [compareMode, history, baseId, targetId]);

  const baseRec   = useMemo(() => history.find((r) => r.id === baseId),   [history, baseId]);
  const targetRec = useMemo(() => history.find((r) => r.id === targetId), [history, targetId]);

  // Line-level diff parts: [{added?, removed?, value}].
  const diffParts = useMemo(() => {
    if (!compareMode || !baseRec || !targetRec) return [];
    return diffLines(baseRec.report || "", targetRec.report || "");
  }, [compareMode, baseRec, targetRec]);

  // Plain-text counts for the header pill.
  const diffStats = useMemo(() => {
    if (diffParts.length === 0) return null;
    let added = 0, removed = 0;
    diffParts.forEach((p) => {
      const n = (p.value.match(/\n/g) || []).length || 1;
      if (p.added)   added   += n;
      if (p.removed) removed += n;
    });
    return { added, removed };
  }, [diffParts]);

  // "Critical findings delta" — how many `## High-priority` bullets does each
  // report carry?  resolved = present in base but missing in target; new =
  // present in target but missing in base; same = exact-string match.
  const findingsDelta = useMemo(() => {
    const grab = (md) => {
      if (!md) return [];
      const m = md.match(/##\s+High[- ]priority([\s\S]*?)(?=\n##\s|$)/i);
      if (!m) return [];
      return m[1].split("\n")
        .map((l) => l.trim())
        .filter((l) => /^-\s+(?!\[)/.test(l))   // bullets only, skip [ ]/[x] checks
        .map((l) => l.replace(/^-\s+/, "").toLowerCase());
    };
    if (!baseRec || !targetRec) return null;
    const a = grab(baseRec.report);
    const b = grab(targetRec.report);
    const aSet = new Set(a);
    const bSet = new Set(b);
    const resolved = a.filter((x) => !bSet.has(x)).length;
    const added    = b.filter((x) => !aSet.has(x)).length;
    return { resolved, added, baseCount: a.length, targetCount: b.length };
  }, [baseRec, targetRec]);

  const deleteHistory = async (rec) => {
    if (!window.confirm("Delete this audit report?")) return;
    try {
      await api.delete(`/projects/${projectId}/audit-history/${rec.id}`);
      loadHistory();
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  if (!open) return null;

  const copyReport = async () => {
    try {
      await navigator.clipboard.writeText(report || "");
      toast.success("Audit copied to clipboard");
    } catch { toast.error("Copy failed"); }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-6"
      onClick={onClose}
      data-testid="ai-audit-modal"
    >
      <div
        className="bg-[#0a0d12] border border-[#00E5FF]/30 w-full max-w-3xl max-h-[88vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div>
            <div className="label-tag">// AI · plan review</div>
            <h3 className="font-display text-lg font-bold inline-flex items-center gap-2">
              <Brain size={20} weight="duotone" /> Design Auditor — Claude Sonnet 4.5
            </h3>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => { setCompareMode(!compareMode); setShowHistory(false); }}
              className={`btn-ghost text-xs py-1.5 inline-flex items-center gap-1 ${compareMode ? "!bg-[#FFCC00]/20 !text-[#FFCC00]" : ""}`}
              disabled={history.length < 2 && !compareMode}
              title={history.length < 2 ? "Need at least two saved reports to diff" : "Compare two saved reports side-by-side"}
              data-testid="ai-audit-compare-toggle"
            >
              <GitDiff size={14} /> {compareMode ? "Exit compare" : "Compare"}
            </button>
            <button
              onClick={() => setShowHistory(!showHistory)}
              className={`btn-ghost text-xs py-1.5 inline-flex items-center gap-1 ${showHistory ? "!bg-[#00E5FF]/20 !text-[#00E5FF]" : ""}`}
              data-testid="ai-audit-history-toggle"
            >
              History ({history.length})
            </button>
            <button
              onClick={run}
              disabled={busy}
              className="btn-ghost text-xs py-1.5 inline-flex items-center gap-1"
              data-testid="ai-audit-rerun"
            >
              <ArrowsClockwise size={14} /> Re-run
            </button>
            <button
              onClick={copyReport}
              disabled={!report}
              className="btn-ghost text-xs py-1.5 inline-flex items-center gap-1"
              data-testid="ai-audit-copy"
            >
              <Copy size={14} /> Copy
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white p-1"
              data-testid="ai-audit-close"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="overflow-auto p-5 flex-1" data-testid="ai-audit-body">
          {compareMode ? (
            <div data-testid="ai-audit-compare">
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <div className="label-tag mb-1 text-[10px]">Base (older)</div>
                  <select value={baseId} onChange={(e) => setBaseId(e.target.value)}
                          className="input-base text-xs w-full"
                          data-testid="ai-audit-compare-base">
                    <option value="">— pick a saved report —</option>
                    {history.map((r) => (
                      <option key={r.id} value={r.id} disabled={r.id === targetId}>
                        {new Date(r.generated_at).toLocaleString()} · {r.by_user_email}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <div className="label-tag mb-1 text-[10px]">Target (newer)</div>
                  <select value={targetId} onChange={(e) => setTargetId(e.target.value)}
                          className="input-base text-xs w-full"
                          data-testid="ai-audit-compare-target">
                    <option value="">— pick a saved report —</option>
                    {history.map((r) => (
                      <option key={r.id} value={r.id} disabled={r.id === baseId}>
                        {new Date(r.generated_at).toLocaleString()} · {r.by_user_email}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              {diffStats && (
                <div className="text-[11px] font-mono mb-2 inline-flex items-center gap-3 flex-wrap"
                     data-testid="ai-audit-compare-stats">
                  <span className="text-emerald-400">+{diffStats.added} added</span>
                  <span className="text-red-400">−{diffStats.removed} removed</span>
                  {diffStats.added === 0 && diffStats.removed === 0 && (
                    <span className="text-gray-500">— reports are identical</span>
                  )}
                  {findingsDelta && (findingsDelta.baseCount > 0 || findingsDelta.targetCount > 0) && (
                    <span className="inline-flex items-center gap-2 ml-2 px-2 py-0.5 border border-[#FFCC00]/40 bg-[#FFCC00]/10 text-[#FFCC00]"
                          data-testid="ai-audit-findings-delta"
                          title="Critical findings (## High-priority bullets) delta">
                      Critical findings:
                      <span className="text-emerald-300">{findingsDelta.resolved} resolved</span>
                      ·
                      <span className="text-red-300">{findingsDelta.added} new</span>
                      <span className="text-gray-400">({findingsDelta.baseCount} → {findingsDelta.targetCount})</span>
                    </span>
                  )}
                </div>
              )}
              {(!baseRec || !targetRec) ? (
                <div className="text-xs text-gray-500 font-mono border border-white/10 p-4">
                  Pick a base and a target above to see the diff.
                </div>
              ) : (
                <div className="border border-white/10 bg-black/40 p-3 max-h-[60vh] overflow-auto font-mono text-[12px] whitespace-pre-wrap"
                     data-testid="ai-audit-compare-diff">
                  {diffParts.map((p, i) => (
                    <span key={i}
                          className={
                            p.added   ? "block bg-emerald-500/10 text-emerald-300 border-l-2 border-emerald-500 pl-2"
                          : p.removed ? "block bg-red-500/10 text-red-300 border-l-2 border-red-500 pl-2 line-through opacity-90"
                          :             "block text-gray-400 pl-2"}>
                      {(p.added ? "+ " : p.removed ? "- " : "  ") + p.value.replace(/\n$/, "")}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <>
              {showHistory && (
            <div className="mb-4 border border-white/10" data-testid="ai-audit-history-list">
              <div className="label-tag p-3 border-b border-white/10">// Saved reports ({history.length})</div>
              <ul className="divide-y divide-white/5 max-h-72 overflow-auto">
                {history.length === 0 && (
                  <li className="p-3 text-xs text-gray-500 font-mono">No prior reports — running one will save it here automatically.</li>
                )}
                {history.map((rec) => (
                  <li key={rec.id} className="flex items-center gap-2 p-2 hover:bg-white/5">
                    <button onClick={() => pickHistory(rec)}
                            className="flex-1 text-left text-xs font-mono text-gray-300"
                            data-testid={`ai-audit-history-${rec.id}`}>
                      <div className="text-[#00E5FF]">{new Date(rec.generated_at).toLocaleString()}</div>
                      <div className="text-[10px] text-gray-500 truncate">{rec.by_user_email}</div>
                    </button>
                    <button onClick={() => deleteHistory(rec)} className="text-gray-500 hover:text-red-400 p-1">
                      <X size={12} />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {busy && (
            <div className="flex items-center gap-3 text-gray-400 text-sm font-mono py-12 justify-center" data-testid="ai-audit-busy">
              <Spinner size={20} className="animate-spin" />
              Running plan review… (≤15 s)
            </div>
          )}
          {error && (
            <div className="border border-red-500/40 bg-red-500/5 text-red-300 text-sm font-mono p-3" data-testid="ai-audit-error">
              {error}
            </div>
          )}
          {report && (
            <div
              className="prose prose-invert font-mono"
              dangerouslySetInnerHTML={{ __html: mdToHtml(report) }}
              data-testid="ai-audit-report"
            />
          )}
            </>
          )}
        </div>

        <div className="px-5 py-3 border-t border-white/10 text-[10px] text-gray-500 font-mono">
          Generated by Claude Sonnet 4.5. AI is a second pair of eyes, not a stamping engineer — always verify against the governing code.
        </div>
      </div>
    </div>
  );
}
